import * as React from "react"

type UseTruncatedTextReturn = {
  ref: React.RefObject<HTMLElement>
  isTruncated: boolean
  recompute: () => void
}

export function useTruncatedText(): UseTruncatedTextReturn {
  const ref = React.useRef<HTMLElement>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)

  const recompute = React.useCallback(() => {
    const element = ref.current
    if (!element) {
      setIsTruncated(false)
      return
    }

    const hasHorizontalOverflow = element.scrollWidth > element.clientWidth
    const hasVerticalOverflow = element.scrollHeight > element.clientHeight

    setIsTruncated(hasHorizontalOverflow || hasVerticalOverflow)
  }, [])

  React.useEffect(() => {
    recompute()
  }, [recompute])

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const element = ref.current
    if (!element) {
      return
    }

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => recompute())
      observer.observe(element)
      return () => observer.disconnect()
    }

    const handleResize = () => recompute()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [recompute])

  return { ref, isTruncated, recompute }
}